import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { fuzzyFilter } from '@hcd/domain'
import { Building2, LayoutGrid, List, Plus, Search, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { NewCustomerDialog } from '@/components/customers/new-customer-dialog'
import { CustomerCards, CustomerList } from '@/components/customers/views'
import { PageHeader } from '@/components/shell/page'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { customerListQuery, type CustomerListItem } from '@/lib/customers'
import { useTitle } from '@/lib/use-title'
import { cn } from '@/lib/utils'

const FILTERS = [
  ['current', 'Current', (c: CustomerListItem) => c.status !== 'archived'],
  ['pending', 'Pending', (c: CustomerListItem) => c.status === 'prospect'],
  ['active', 'Active clients', (c: CustomerListItem) => c.status === 'client'],
  ['former', 'Former', (c: CustomerListItem) => c.status === 'archived'],
  ['all', 'All', () => true],
] as const
type FilterKey = (typeof FILTERS)[number][0]

const VIEWS = [
  ['list', 'List', List],
  ['cards', 'Cards', LayoutGrid],
] as const
type View = (typeof VIEWS)[number][0]

const VIEW_KEY = 'hcd.customers.view'
const storedView = (): View | undefined => {
  try {
    const v = localStorage.getItem(VIEW_KEY)
    return v === 'list' || v === 'cards' ? v : undefined
  } catch {
    return undefined
  }
}

export const Route = createFileRoute('/_app/customers/')({
  validateSearch: z.object({
    show: z.enum(FILTERS.map((f) => f[0]) as [FilterKey, ...FilterKey[]]).optional(),
    view: z.enum(['list', 'cards']).optional(),
    q: z.string().max(100).optional(),
  }),
  component: CustomersPage,
})

function CustomersPage() {
  useTitle('Customers')
  const search = Route.useSearch()
  const nav = Route.useNavigate()
  const navigate = useNavigate()
  const show: FilterKey = search.show ?? 'current'
  const view: View = search.view ?? storedView() ?? 'list'
  const [text, setText] = useState(search.q ?? '')
  const [adding, setAdding] = useState(false)
  const { data, isPending, isError, error } = useQuery(customerListQuery())

  useEffect(() => {
    const t = setTimeout(() => {
      if ((search.q ?? '') !== text)
        nav({ search: (s) => ({ ...s, q: text || undefined }), replace: true })
    }, 200)
    return () => clearTimeout(t)
  }, [text, search.q, nav])

  const setSearch = (patch: Partial<typeof search>) =>
    nav({ search: (s) => ({ ...s, ...patch }), replace: true })
  const setView = (v: View) => {
    try {
      localStorage.setItem(VIEW_KEY, v)
    } catch {
      /* private mode: fine */
    }
    setSearch({ view: v })
  }

  const all = data ?? []
  const base = useMemo(
    () =>
      search.q
        ? fuzzyFilter(all, search.q, (c) => [
            c.name,
            c.reference,
            c.primaryContact?.name ?? '',
            c.primaryContact?.email ?? '',
            c.billingEmail,
            c.mainEmail,
            c.mainSite?.postcode ?? '',
            c.mainSite?.town ?? '',
          ])
        : all,
    [all, search.q],
  )
  const rows = base.filter(FILTERS.find((f) => f[0] === show)![2])

  return (
    <>
      <PageHeader
        title="Customers"
        description="Everyone you quote for. New customers show as Pending with just the basics; the client fills in most of the rest when they sign."
        actions={
          <Button className="h-10" onClick={() => setAdding(true)}>
            <Plus />
            New customer
          </Button>
        }
      />

      <div className="mb-4 grid gap-3 rounded-xl border bg-card p-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            aria-label="Search customers"
            placeholder="Search by name, account number, contact or postcode"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="h-11 pl-9 text-base md:h-10 md:text-[15px]"
          />
        </div>
        <div
          role="radiogroup"
          aria-label="View"
          className="inline-flex w-full rounded-lg border bg-muted p-1 md:w-auto"
        >
          {VIEWS.map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={view === key}
              onClick={() => setView(key)}
              className={cn(
                'inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium md:flex-none',
                view === key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="size-4" aria-hidden />
              {label}
            </button>
          ))}
        </div>
      </div>

      <nav aria-label="Customer status" className="-mx-4 mb-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <ul className="flex w-max gap-2">
          {FILTERS.map(([key, label, fn]) => (
            <li key={key}>
              <button
                type="button"
                aria-pressed={show === key}
                onClick={() => setSearch({ show: key === 'current' ? undefined : key })}
                className={cn(
                  'h-9 rounded-full border px-3.5 text-sm font-medium whitespace-nowrap',
                  show === key
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'bg-card hover:bg-secondary',
                )}
              >
                {label}{' '}
                <span
                  className={cn(
                    'tabular',
                    show === key ? 'text-primary-foreground/80' : 'text-muted-foreground',
                  )}
                >
                  {base.filter(fn).length}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {search.q && (
        <p
          className="mb-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
          aria-live="polite"
        >
          {rows.length} {rows.length === 1 ? 'customer matches' : 'customers match'}
          <button
            type="button"
            className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            onClick={() => {
              setText('')
              setSearch({ q: undefined })
            }}
          >
            <X className="size-3.5" aria-hidden />
            Clear search
          </button>
        </p>
      )}

      {isError ? (
        <p role="alert" className="text-destructive">
          {error.message}
        </p>
      ) : isPending ? (
        <div className="grid gap-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      ) : all.length === 0 ? (
        <Empty className="rounded-xl border bg-card py-12">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Building2 />
            </EmptyMedia>
            <EmptyTitle>No customers yet</EmptyTitle>
            <EmptyDescription>
              Add a customer to start a quote. You only need a name and a contact.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button className="h-10" onClick={() => setAdding(true)}>
              <Plus />
              New customer
            </Button>
          </EmptyContent>
        </Empty>
      ) : rows.length === 0 ? (
        <p className="rounded-xl border bg-card p-6 text-[15px] text-muted-foreground">
          {search.q
            ? 'Nothing matches your search. Try fewer or different words.'
            : 'No customers with this status.'}
        </p>
      ) : view === 'cards' ? (
        <CustomerCards rows={rows} />
      ) : (
        <CustomerList rows={rows} />
      )}

      <NewCustomerDialog
        open={adding}
        onOpenChange={setAdding}
        onCreated={(c) => {
          setAdding(false)
          navigate({ to: '/customers/$id', params: { id: c.customer.id } })
        }}
      />
    </>
  )
}
